'use client';

import { useState, useEffect } from 'react';
import { CheckIcon, CircleIcon } from './icons/IconSystem';

interface PasswordStrengthIndicatorProps {
  password: string;
  className?: string;
}

interface StrengthCriteria {
  label: string;
  test: (password: string) => boolean;
  color: string;
}

const strengthCriteria: StrengthCriteria[] = [
  {
    label: '至少8个字符',
    test: (password: string) => password.length >= 8,
    color: '#ff4444'
  },
  {
    label: '包含大写字母',
    test: (password: string) => /[A-Z]/.test(password),
    color: '#ff8800'
  },
  {
    label: '包含小写字母',
    test: (password: string) => /[a-z]/.test(password),
    color: '#ffaa00'
  },
  {
    label: '包含数字',
    test: (password: string) => /\d/.test(password),
    color: '#00aa00'
  },
  {
    label: '包含特殊字符',
    test: (password: string) => /[!@#$%^&*()_+\-=\[\]{};':"\\|,.<>\/?]/.test(password),
    color: '#008800'
  }
];

export default function PasswordStrengthIndicator({ password, className = '' }: PasswordStrengthIndicatorProps) {
  const [strength, setStrength] = useState(0);
  const [passedCriteria, setPassedCriteria] = useState<boolean[]>([]);

  useEffect(() => {
    if (!password) {
      setStrength(0);
      setPassedCriteria(new Array(strengthCriteria.length).fill(false));
      return;
    }

    const passed = strengthCriteria.map(criteria => criteria.test(password));
    setPassedCriteria(passed);
    
    const passedCount = passed.filter(Boolean).length;
    const strengthPercentage = (passedCount / strengthCriteria.length) * 100;
    setStrength(strengthPercentage);
  }, [password]);

  const getStrengthLabel = () => {
    if (strength === 0) return '请输入密码';
    if (strength <= 20) return '很弱';
    if (strength <= 40) return '弱';
    if (strength <= 60) return '一般';
    if (strength <= 80) return '强';
    return '很强';
  };

  const getStrengthColor = () => {
    if (strength === 0) return '#e0e0e0';
    if (strength <= 20) return '#ff4444';
    if (strength <= 40) return '#ff8800';
    if (strength <= 60) return '#ffaa00';
    if (strength <= 80) return '#00aa00';
    return '#008800';
  };

  if (!password) return null;

  return (
    <div className={`password-strength ${className}`}>
      {/* 强度条 */}
      <div className="strength-bar-container">
        <div 
          className="strength-bar"
          style={{
            width: `${strength}%`,
            backgroundColor: getStrengthColor(),
            transition: 'all 0.3s ease'
          }}
        />
      </div>
      
      {/* 强度标签 */}
      <div className="strength-label" style={{ color: getStrengthColor() }}>
        {getStrengthLabel()}
      </div>
      
      {/* 详细标准 */}
      <div className="strength-criteria">
        {strengthCriteria.map((criteria, index) => (
          <div 
            key={index} 
            className="criteria-item"
            style={{ 
              color: passedCriteria[index] ? criteria.color : '#999999',
              fontSize: '12px',
              marginTop: '4px',
              display: 'flex',
              alignItems: 'center',
              gap: '6px'
            }}
          >
            <span style={{ fontSize: '14px' }}>
                              {passedCriteria[index] ? <CheckIcon size={16} color="#22c55e" /> : <CircleIcon size={16} color="#999" />}
            </span>
            {criteria.label}
          </div>
        ))}
      </div>
    </div>
  );
} 